import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Fab,
  Autocomplete,
  Grid,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { TopeDescuentoClienteRubro, TopeDescuentoClienteRubroRequest, RubroResponse } from '../../types';
import { getEndOfDayISOString } from '../../utils/date';

const TopesClienteRubro: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTope, setEditingTope] = useState<TopeDescuentoClienteRubro | null>(null);
  const [formData, setFormData] = useState<TopeDescuentoClienteRubroRequest>({
    clienteId: '',
    rubroCodigo: '',
    rubroNombre: '',
    topeDescuento: 0,
    fechaVigencia: new Date().toISOString().split('T')[0],
    activo: true,
  });

  const [topeDescuentoText, setTopeDescuentoText] = useState('');
  const [filtro, setFiltro] = useState('');
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Consultar topes por cliente-rubro
  const { data: topes = [], isLoading, error } = useQuery({
    queryKey: ['topesClienteRubro'],
    queryFn: () => apiService.obtenerTopesClienteRubro(),
  });

  // Consultar rubros disponibles
  const { data: rubros = [] } = useQuery({
    queryKey: ['rubros'],
    queryFn: () => apiService.obtenerRubrosDisponibles(),
  });

  // Mutaciones
  const createMutation = useMutation({
    mutationFn: (data: TopeDescuentoClienteRubroRequest) => apiService.crearTopeClienteRubro(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesClienteRubro'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TopeDescuentoClienteRubroRequest }) =>
      apiService.actualizarTopeClienteRubro(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesClienteRubro'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.eliminarTopeClienteRubro(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesClienteRubro'] });
    },
  });

  const handleOpenDialog = (tope?: TopeDescuentoClienteRubro) => {
    if (tope) {
      setEditingTope(tope);
      setFormData({
        clienteId: tope.clienteId,
        rubroCodigo: tope.rubroCodigo,
        rubroNombre: tope.rubroNombre,
        topeDescuento: tope.topeDescuento,
        fechaVigencia: tope.fechaVigencia.split('T')[0],
        activo: tope.activo,
      });
      setTopeDescuentoText(tope.topeDescuento.toString());
    } else {
      setEditingTope(null);
      setFormData({
        clienteId: '',
        rubroCodigo: '',
        rubroNombre: '',
        topeDescuento: 0,
        fechaVigencia: new Date().toISOString().split('T')[0],
        activo: true,
      });
      setTopeDescuentoText('');
    }
    setErrorMensaje(null); // Limpiar mensaje de error al abrir
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTope(null);
    setFormData({
      clienteId: '',
      rubroCodigo: '',
      rubroNombre: '',
      topeDescuento: 0,
      fechaVigencia: new Date().toISOString().split('T')[0],
      activo: true,
    });
    setTopeDescuentoText('');
    setErrorMensaje(null); // Limpiar mensaje de error al cerrar
  };

  const handleRubroChange = (rubro: RubroResponse | null) => {
    if (rubro) {
      setFormData({
        ...formData,
        rubroCodigo: rubro.codigo,
        rubroNombre: rubro.codigo, // Usar el código como nombre por defecto
      });
    }
  };

  const handleTopeDescuentoChange = (value: string) => {
    setTopeDescuentoText(value);
    
    // Validar que sea un número válido
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setFormData({ ...formData, topeDescuento: numValue });
    } else if (value === '' || value === '.') {
      // Permitir campo vacío o punto decimal
      setFormData({ ...formData, topeDescuento: 0 });
    }
  };

  const handleSubmit = () => {
    setErrorMensaje(null); // Limpiar mensaje de error previa
    // Validar existencia solo si es nuevo
    if (!editingTope) {
      const clienteIdForm = formData.clienteId.trim().toLowerCase();
      const rubroCodigoForm = formData.rubroCodigo.trim().toLowerCase();
      const topeExistente = topes.find(
        (tope: TopeDescuentoClienteRubro) =>
          tope.clienteId.trim().toLowerCase() === clienteIdForm &&
          tope.rubroCodigo.trim().toLowerCase() === rubroCodigoForm
      );
      if (topeExistente) {
        setErrorMensaje(
          `Ya existe un tope de descuento para el cliente con ID: ${topeExistente.clienteId} y rubro: ${topeExistente.rubroCodigo}.`
        );
        return;
      }
    }
    // Convertir la fecha a formato ISO para que el backend la pueda parsear correctamente
    const requestData = {
      ...formData,
      fechaVigencia: getEndOfDayISOString(formData.fechaVigencia),
      activo: formData.activo
    };
    
    if (editingTope) {
      updateMutation.mutate({ id: editingTope.id, data: requestData });
    } else {
      createMutation.mutate(requestData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tope de descuento?')) {
      deleteMutation.mutate(id);
    }
  };

  // Función utilitaria para mostrar la fecha en formato DD/MM/YYYY (UTC)
  function formatFechaLocal(fechaVigencia: string) {
    const date = new Date(fechaVigencia);
    return date.getUTCDate().toString().padStart(2, '0') + '/' +
           (date.getUTCMonth() + 1).toString().padStart(2, '0') + '/' +
           date.getUTCFullYear();
  }

  // Función utilitaria para comparar solo la fecha (ignorando la hora y zona horaria)
  function isVigente(fechaVigencia: string) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const vigencia = new Date(fechaVigencia);
    vigencia.setHours(0, 0, 0, 0);
    return vigencia >= hoy;
  }

  // Filtrado de topes según el texto ingresado
  const topesFiltrados = topes.filter((tope) => {
    const texto = filtro.toLowerCase();
    return (
      tope.clienteRazonSocial?.toLowerCase().includes(texto) ||
      tope.clienteId?.toLowerCase().includes(texto) ||
      tope.rubroCodigo?.toLowerCase().includes(texto) ||
      tope.rubroNombre?.toLowerCase().includes(texto) ||
      String(tope.topeDescuento).toLowerCase().includes(texto) ||
      formatFechaLocal(tope.fechaVigencia).includes(texto) ||
      (tope.activo ? 'activo' : 'inactivo').includes(texto)
    );
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error al cargar los topes de descuento por cliente y rubro
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Topes de Descuento por Cliente y Rubro
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Tope
        </Button>
      </Box>
      {/* Filtro de búsqueda */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Filtrar"
          variant="outlined"
          size="small"
          fullWidth
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Buscar por cualquier campo..."
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>ID Cliente</TableCell>
              <TableCell>Rubro</TableCell>
              <TableCell>Código Rubro</TableCell>
              <TableCell align="right">Tope (%)</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topesFiltrados.map((tope) => (
              <TableRow key={tope.id}>
                <TableCell>{tope.clienteRazonSocial}</TableCell>
                <TableCell>{tope.clienteId}</TableCell>
                <TableCell>{tope.rubroNombre}</TableCell>
                <TableCell>{tope.rubroCodigo}</TableCell>
                <TableCell align="right">
                  <Typography variant="h6" color="primary">
                    {tope.topeDescuento}%
                  </Typography>
                </TableCell>
                <TableCell>
                  {formatFechaLocal(tope.fechaVigencia)}
                </TableCell>
                <TableCell>
                  {isVigente(tope.fechaVigencia) ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Vigente"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon />}
                      label="Vencido"
                      color="warning"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {tope.activo ? (
                    <Chip label="Activo" color="success" size="small" />
                  ) : (
                    <Chip label="Inactivo" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(tope)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(tope.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTope ? 'Editar Tope de Descuento por Rubro' : 'Nuevo Tope de Descuento por Rubro'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {errorMensaje && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {errorMensaje}
              </Alert>
            )}
            <TextField
              fullWidth
              label="ID del Cliente"
              value={formData.clienteId}
              onChange={(e) => setFormData({ ...formData, clienteId: e.target.value })}
              margin="normal"
              required
              disabled={!!editingTope}
            />
            <TextField
              fullWidth
              label="Código del Rubro"
              value={formData.rubroCodigo}
              onChange={(e) => setFormData({ ...formData, rubroCodigo: e.target.value })}
              margin="normal"
              required
              disabled={!!editingTope}
            />
            <TextField
              fullWidth
              label="Nombre del Rubro"
              value={formData.rubroNombre}
              onChange={(e) => setFormData({ ...formData, rubroNombre: e.target.value })}
              margin="normal"
              required
              disabled={!!editingTope}
            />
            <TextField
              fullWidth
              label="Tope de Descuento (%)"
              type="text"
              value={topeDescuentoText}
              onChange={(e) => handleTopeDescuentoChange(e.target.value)}
              margin="normal"
              required
              placeholder="0.00"
              helperText="Ingrese un valor entre 0 y 100"
              error={topeDescuentoText !== '' && (isNaN(parseFloat(topeDescuentoText)) || parseFloat(topeDescuentoText) < 0 || parseFloat(topeDescuentoText) > 100)}
            />
            <TextField
              fullWidth
              label="Fecha de Vigencia"
              type="date"
              value={formData.fechaVigencia}
              onChange={(e) => setFormData({ ...formData, fechaVigencia: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  color="primary"
                />
              }
              label="Tope Activo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.clienteId || !formData.rubroCodigo || !formData.rubroNombre || createMutation.isPending || updateMutation.isPending || (topeDescuentoText !== '' && (isNaN(parseFloat(topeDescuentoText)) || parseFloat(topeDescuentoText) < 0 || parseFloat(topeDescuentoText) > 100))}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              editingTope ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB para agregar rápidamente */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog()}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default TopesClienteRubro;
